/**
 * Per-app overview at /apps/:slug.
 *
 * Two render shapes, picked by `is_manager`:
 *
 *   - Manager: rich page — URLs (live + preview), visibility, managers,
 *     app roles, recent deploys, promote-preview CTA. The per-app tab
 *     bar exposes every deeper surface.
 *
 *   - Non-manager: a single calm page — the live app link, visibility,
 *     who the managers are, and (if external paths are enabled) an
 *     inline token-management section. No tab nav, no deploy history,
 *     no app roles. Everything they need to use the app, nothing they
 *     don't.
 *
 * The two views share one loader + one set of cards so the data model
 * stays straightforward.
 */

import { Link, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';
import type { AppManager, AppRoleGrant, AppTokenSummary, DeploymentRow } from '../api/types.js';
import { AppTabs } from '../components/AppTabs.js';
import {
  IconBot,
  IconChevronDown,
  IconExternal,
  IconGlobe,
  IconLock,
  IconRocket,
  IconSparkle,
} from '../components/Icons.js';
import { Avatar } from '../components/Avatar.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { UserTokenSection } from '../components/UserTokenSection.js';
import { ExternalAppSection } from '../components/ExternalAppSection.js';
import {
  AnchorButton,
  Banner,
  ButtonLink,
  Card,
  CardHeader,
  Mono,
  PageHeader,
  PageSkeleton,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith, type NormalizedApp } from '../lib/useAppPage.js';
import { type ActionMessage } from '../lib/runAction.js';

interface AppDetailPayload {
  managers: AppManager[];
  roles: AppRoleGrant[];
  deployments: DeploymentRow[];
  myTokens: AppTokenSummary[];
}

async function loadAppDetailPayload(slug: string): Promise<AppDetailPayload> {
  // Tokens are fetched alongside roles + deployments so the non-manager
  // branch can render its inline token section without a second waterfall.
  // The cost is one tiny GET that managers don't end up consuming; not
  // worth gating behind a second render to save.
  //
  // Deployments are fetched per env: this page only needs the newest few
  // of each for the recent-versions list and the promote CTA, and a
  // mixed query would let a burst of preview iterations crowd the active
  // live row out of the window entirely.
  const [rolesResp, liveResp, previewResp, tokensResp] = await Promise.all([
    api.get<{ managers: AppManager[]; roles: AppRoleGrant[] }>(
      `/api/apps/${encodeURIComponent(slug)}/roles`,
    ),
    api.get<{ deployments: DeploymentRow[] }>(
      `/api/apps/${encodeURIComponent(slug)}/deployments?env=live&limit=5`,
    ),
    api.get<{ deployments: DeploymentRow[] }>(
      `/api/apps/${encodeURIComponent(slug)}/deployments?env=preview&limit=5`,
    ),
    api.get<{ tokens: AppTokenSummary[] }>(`/api/apps/${encodeURIComponent(slug)}/tokens`),
  ]);
  return {
    managers: rolesResp.managers,
    roles: rolesResp.roles,
    deployments: [...liveResp.deployments, ...previewResp.deployments].sort(
      (a, b) => b.created_at - a.created_at,
    ),
    myTokens: tokensResp.tokens,
  };
}

function UrlRow({ label, host }: { label: string; host: string | null }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-line last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-muted font-medium">{label}</span>
      {host ? (
        <a
          href={`https://${host}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-mono text-ink hover:text-brand transition-colors truncate"
        >
          {host}
          <IconExternal size={12} />
        </a>
      ) : (
        <span className="text-sm text-faint">Not configured</span>
      )}
    </div>
  );
}

/**
 * Split button for the edit CTA in the manager header.
 *
 *   ┌──────────────────────┬─────┐
 *   │  ✦ Edit in Preview   │  ▼  │
 *   └──────────────────────┴─────┘
 *
 * The primary half keeps the existing behavior — open the preview with
 * the chat panel ready (`?kin=open`). The caret half offers "Open agent
 * only" → /apps/:slug/agent, the standalone chat that doesn't depend on
 * the preview loading at all. That's the escape hatch when the preview
 * redirects or errors and the injected widget never appears. Same
 * hand-rolled pattern as `StartBuildingButton` in Apps.tsx.
 */
function EditSplitButton({
  slug,
  href,
  variant,
}: {
  slug: string;
  href: string;
  variant: 'primary' | 'secondary';
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click outside / Esc → close. Only registered while the menu is open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      <AnchorButton
        href={href}
        target="_blank"
        rel="noreferrer"
        variant={variant}
        size="sm"
        className={
          variant === 'primary' ? 'rounded-r-none border-r border-brand-fg/20' : 'rounded-r-none'
        }
        title="Open the preview with the Kin chat panel ready"
      >
        <IconSparkle size={13} />
        Edit in Preview
      </AnchorButton>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More editing options"
        onClick={() => setOpen((v) => !v)}
        className={
          'h-8 px-2 inline-flex items-center justify-center rounded-r-lg font-medium transition-colors ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
          (variant === 'primary'
            ? 'bg-brand text-brand-fg hover:bg-brand-hover'
            : 'bg-surface text-ink border border-line border-l-0 hover:bg-surface-2')
        }
      >
        <IconChevronDown size={14} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-64 rounded-lg border border-line bg-surface shadow-lg z-20 py-1 animate-fade-in"
        >
          <Link
            to={`/apps/${slug}/agent`}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 px-3 py-2 text-sm text-ink hover:bg-surface-2 transition-colors"
          >
            <IconBot size={14} className="text-brand mt-0.5 shrink-0" />
            <span>
              <span className="block font-medium">Open agent only</span>
              <span className="block text-xs text-muted mt-0.5">
                Chat with Kin without loading the preview
              </span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}

export function AppDetail() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload } = useAppPageWith(slug, loadAppDetailPayload);

  if (loading) return <PageSkeleton rows={6} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;

  return app.is_manager ? (
    <ManagerView app={app} payload={payload} reload={reload} />
  ) : (
    <UserView app={app} payload={payload} reload={reload} />
  );
}

// ---------- Manager view ---------------------------------------------------

function ManagerView({
  app,
  payload,
  reload,
}: {
  app: NormalizedApp;
  payload: AppDetailPayload;
  reload: () => Promise<void>;
}) {
  const { managers, roles, deployments } = payload;
  const isExternal = app.kind === 'external';
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const liveUrl = app.cf_custom_domain_live ? `https://${app.cf_custom_domain_live}` : null;
  // Preview URL with `?kin=open` — the preview-widget bootstrap picks
  // up that query param, sets sessionStorage, strips it from the URL,
  // and mounts with the chat panel already expanded. Same flow the
  // post-create redirect uses.
  const editInPreviewUrl = app.cf_custom_domain_preview
    ? `https://${app.cf_custom_domain_preview}/?kin=open`
    : null;

  // Surface a CTA when the latest preview is newer than what's live —
  // i.e. there's iteration that hasn't shipped. `source_preview_version`
  // on a live row tracks which preview it came from; comparing that to
  // the latest preview row's version tells us whether work is pending.
  const latestPreview = deployments.find((d) => d.env === 'preview');
  const activeLive = deployments.find((d) => d.env === 'live' && d.status === 'active');
  const promotablePreview =
    latestPreview &&
    latestPreview.status !== 'failed' &&
    (!activeLive || (activeLive.source_preview_version ?? 0) < latestPreview.version);

  // External apps have no platform-managed preview/live deploy lifecycle —
  // they're registered against caller-owned hostnames and use bearer-token
  // auth. Render a focused page: identity + hostnames + the bearer
  // token/paths admin panel. Managed apps fall through to the rich view.
  if (isExternal) {
    const hostnames = app.external_hostnames ?? [];
    return (
      <div className="animate-fade-in max-w-3xl">
        <PageHeader
          back={{ to: '/', label: 'Apps' }}
          eyebrow="External app"
          title={
            <span className="flex items-center gap-3 flex-wrap">
              <span>{app.name}</span>
              <StatusBadge status={app.status} />
            </span>
          }
          description={app.description || undefined}
        />

        <div className="mb-6 text-xs text-muted">
          <Mono>{app.slug}</Mono>
        </div>

        {actionMessage && (
          <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
            {actionMessage.text}
          </Banner>
        )}

        <Card className="mb-5">
          <CardHeader title="Hostnames" description="Where this external app is reachable." />
          {hostnames.length === 0 ? (
            <p className="px-5 py-4 text-sm text-faint">No hostnames registered.</p>
          ) : (
            <ul className="divide-y divide-line">
              {hostnames.map((h) => (
                <li key={h} className="px-5 py-3 text-sm">
                  <a
                    href={`https://${h}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 font-mono text-ink hover:text-brand transition-colors break-all"
                  >
                    {h}
                    <IconExternal size={12} />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <ExternalAppSection
          app={app}
          busy={busy}
          setBusy={setBusy}
          setActionMessage={setActionMessage}
          reload={reload}
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: '/', label: 'Apps' }}
        eyebrow={app.visibility === 'workspace' ? 'Mytra-wide app' : 'Private app'}
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span>{app.name}</span>
            <StatusBadge status={app.status} />
          </span>
        }
        description={app.description || undefined}
        actions={
          editInPreviewUrl || liveUrl ? (
            <>
              {editInPreviewUrl && (
                <EditSplitButton
                  slug={app.slug}
                  href={editInPreviewUrl}
                  // "Edit in Preview" is the primary CTA only when the app
                  // hasn't been promoted yet — for already-shipped apps,
                  // "Open app" stays primary and Edit drops to secondary.
                  variant={liveUrl ? 'secondary' : 'primary'}
                />
              )}
              {liveUrl && (
                <AnchorButton
                  href={liveUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="primary"
                  size="sm"
                >
                  Open app
                  <IconExternal size={13} />
                </AnchorButton>
              )}
            </>
          ) : undefined
        }
      />

      <div className="mb-6 text-xs text-muted">
        <Mono>{app.slug}</Mono>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0">
          {promotablePreview && (
            <div className="mb-5 rounded-xl border border-brand/30 bg-brand-soft/40 px-5 py-4 flex items-center gap-4 flex-wrap">
              <div className="h-10 w-10 rounded-lg bg-brand/15 text-brand flex-none flex items-center justify-center">
                <IconRocket size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink text-sm">
                  Preview v{latestPreview.version} hasn&apos;t been published yet
                </p>
                <p className="text-muted text-xs mt-0.5">
                  {activeLive
                    ? `Live is on v${activeLive.source_preview_version ?? activeLive.version}. Promote when you're happy with the preview.`
                    : "This app has never been published. Promote when you're happy with the preview."}
                  {latestPreview.ai_summary && (
                    <>
                      {' '}
                      &mdash; <em>{latestPreview.ai_summary}</em>
                    </>
                  )}
                </p>
              </div>
              <ButtonLink to={`/apps/${app.slug}/versions`} variant="primary" size="sm">
                <IconRocket size={13} />
                Promote to live
              </ButtonLink>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader title="URLs" description="Where the app is reachable." />
              <UrlRow label="Live" host={app.cf_custom_domain_live} />
              <UrlRow label="Preview" host={app.cf_custom_domain_preview} />
            </Card>

            <Card>
              <CardHeader
                title={
                  <span className="inline-flex items-center gap-1.5">
                    {app.visibility === 'workspace' ? (
                      <IconGlobe size={14} className="text-info" />
                    ) : (
                      <IconLock size={14} className="text-muted" />
                    )}
                    Visibility
                  </span>
                }
                actions={
                  <ButtonLink to={`/apps/${app.slug}/share`} variant="ghost" size="sm">
                    Manage access
                  </ButtonLink>
                }
              />
              <div className="px-5 py-4 text-sm">
                {app.visibility === 'workspace' ? (
                  <p className="text-ink">
                    Anyone at Mytra can open this app. Managing it still requires being a manager.
                  </p>
                ) : (
                  <p className="text-ink">
                    Private. Only the managers and app-role holders below can reach it.
                  </p>
                )}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <Card>
              <CardHeader
                title="Managers"
                description="Can deploy, configure, and manage access."
                actions={
                  <ButtonLink to={`/apps/${app.slug}/share`} variant="ghost" size="sm">
                    Manage
                  </ButtonLink>
                }
              />
              {managers.length === 0 ? (
                <p className="px-5 py-5 text-muted text-sm">No managers on this app yet.</p>
              ) : (
                <ul className="divide-y divide-line">
                  {managers.map((m) => (
                    <li key={m.user_id} className="px-5 py-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate inline-flex items-center gap-2">
                          <Avatar user={m} size={22} />
                          <span className="min-w-0 truncate">
                            <span className="font-medium text-ink">{m.name ?? m.email}</span>
                            {m.name && <span className="text-muted ml-2 text-xs">{m.email}</span>}
                          </span>
                        </span>
                        <span className="text-xs text-faint shrink-0">{relTime(m.created_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader
                title="App roles"
                description="Admin / Member — read by the app's own code to gate features."
                actions={
                  <ButtonLink to={`/apps/${app.slug}/share`} variant="ghost" size="sm">
                    Manage
                  </ButtonLink>
                }
              />
              {roles.length === 0 ? (
                <p className="px-5 py-5 text-muted text-sm">
                  No app roles granted. App roles are independent from managers.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {roles.map((r) => (
                    <li key={r.user_id} className="px-5 py-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="min-w-0 truncate inline-flex items-center gap-2">
                          <Avatar user={r} size={22} />
                          <span className="min-w-0 truncate">
                            <span className="font-medium text-ink">{r.name ?? r.email}</span>
                            {r.name && <span className="text-muted ml-2 text-xs">{r.email}</span>}
                          </span>
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-brand-soft text-brand font-medium uppercase tracking-wide">
                          {r.role}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card className="mt-5">
            <CardHeader
              title="Recent deploys"
              description="The last five publishes — preview and live."
              actions={
                <ButtonLink to={`/apps/${app.slug}/versions`} variant="ghost" size="sm">
                  See all
                </ButtonLink>
              }
            />
            {deployments.length === 0 ? (
              <p className="px-5 py-5 text-muted text-sm">No deploys yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {deployments.slice(0, 5).map((d) => (
                  <li key={d.id} className="px-5 py-3 text-sm space-y-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 min-w-0 flex-wrap">
                        <Mono className="uppercase">{d.env}</Mono>
                        <span className="font-semibold text-ink tabular">v{d.version}</span>
                        <StatusBadge status={d.status} />
                      </span>
                      <span className="text-xs text-faint shrink-0">{relTime(d.created_at)}</span>
                    </div>
                    {d.ai_summary && (
                      <p className="text-xs text-muted line-clamp-2 sm:line-clamp-1">
                        {d.ai_summary}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <p className="mt-6 text-xs text-muted">
            <Link to={`/apps/${app.slug}/health`} className="hover:text-ink transition-colors">
              Check health →
            </Link>
          </p>
        </main>
      </div>
    </div>
  );
}

// ---------- Non-manager view ----------------------------------------------

function UserView({
  app,
  payload,
  reload,
}: {
  app: NormalizedApp;
  payload: AppDetailPayload;
  reload: () => Promise<void>;
}) {
  const { managers, myTokens } = payload;
  const liveUrl = app.cf_custom_domain_live ? `https://${app.cf_custom_domain_live}` : null;
  const hasBearerPaths = (app.bearer_paths ?? []).length > 0;
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: '/', label: 'Apps' }}
        eyebrow={app.visibility === 'workspace' ? 'Mytra-wide app' : 'Private app'}
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span>{app.name}</span>
            <StatusBadge status={app.status} />
          </span>
        }
        description={app.description || undefined}
        actions={
          liveUrl ? (
            <AnchorButton
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              variant="primary"
              size="sm"
            >
              Open app
              <IconExternal size={13} />
            </AnchorButton>
          ) : undefined
        }
      />

      {actionMessage && (
        <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
          {actionMessage.text}
        </Banner>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <CardHeader title="Link" description="Open the app in a new tab." />
          {liveUrl ? (
            <div className="px-5 py-4">
              <a
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-mono text-ink hover:text-brand transition-colors break-all"
              >
                {app.cf_custom_domain_live}
                <IconExternal size={12} />
              </a>
            </div>
          ) : (
            <p className="px-5 py-4 text-sm text-faint">Not published yet.</p>
          )}
        </Card>

        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-1.5">
                {app.visibility === 'workspace' ? (
                  <IconGlobe size={14} className="text-info" />
                ) : (
                  <IconLock size={14} className="text-muted" />
                )}
                Visibility
              </span>
            }
          />
          <div className="px-5 py-4 text-sm">
            {app.visibility === 'workspace' ? (
              <p className="text-ink">Mytra-wide — anyone at Mytra can open this app.</p>
            ) : (
              <p className="text-ink">
                Private — only the managers and people they&apos;ve granted access can reach it.
              </p>
            )}
          </div>
        </Card>
      </div>

      <Card className="mt-5">
        <CardHeader title="Managers" description="Who to ask for changes, access, or help." />
        {managers.length === 0 ? (
          <p className="px-5 py-5 text-muted text-sm">No managers listed on this app.</p>
        ) : (
          <ul className="divide-y divide-line">
            {managers.map((m) => (
              <li key={m.user_id} className="px-5 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <Avatar user={m} size={28} />
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-ink">{m.name ?? m.email}</span>
                    {m.name && (
                      <a
                        href={`mailto:${m.email}`}
                        className="text-muted ml-2 text-xs hover:text-ink transition-colors"
                      >
                        {m.email}
                      </a>
                    )}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {hasBearerPaths && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-semibold tracking-tight mb-1">
            Programmatic access
          </h2>
          <p className="text-muted text-sm mb-4">
            Mint a bearer token to call{' '}
            <Mono>{app.cf_custom_domain_live ?? `${app.slug}.mkin.app`}</Mono> from MCP clients,
            scripts, or other services.
          </p>
          <UserTokenSection
            app={app}
            myTokens={myTokens}
            busy={busy}
            setBusy={setBusy}
            setActionMessage={setActionMessage}
            reload={reload}
          />
        </div>
      )}
    </div>
  );
}
